import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  feedbackActivateAdditio,
  getClassroomMaterials,
  getClassGradeAlgorithm,
  getFeedBackResult,
  getNotebook,
  deleteClassroom,
  deleteTask,
  getAvailableClassesByTeacher,
  getClassroomsByTeacherId,
  patchRenameClassroom,
  postJoinClassrooms,
  postReorderClassrooms,
  getGradeAlgorithms,
  patchGradeAlgorithms,
  getGroupedClassesByTeacher,
  getGroupedAvailableClassesByTeacher,
  postCreateClass,
  getAvailableClassesBySchool,
  getClassesByStudent,
  getClassByStudent,
  getClassMembers,
  getTeacherTasksByClassId,
  getActivationAvailableNotebook,
  patchUpdateTask,
  patchActivateTask,
  patchCloseTask,
  patchTaskStudentReview,
  patchUpdateTaskNotification,
  postCreateTask,
  getStudentTasksByClassId,
  getTask,
  getCheckResults,
  getSubmissionsList,
  postCreateDrafTask,
} from 'api/classroom'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import { hideLoader } from 'store/actions/drawer.actions'
import { PROCESS_STATUS, TYPES_ALERTS } from 'utils/constants/global'
import { KEY_HOME } from 'hooks/home'
import { USER_KEYS } from 'hooks/user'
import { TASK_STATUS } from 'utils/constants'

export const CLASSROOM_KEYS = {
  AVAILABLE_CLASSES: 'AVAILABLE_CLASSES',
  AVAILABLE_CLASSES_BY_SCHOOL: 'AVAILABLE_CLASSES_BY_SCHOOL',
  AVAILABLE_NOTEBOOK: 'AVAILABLE_NOTEBOOK',
  CLASSES: 'CLASSES',
  CLASSROOM: 'CLASSROOM',
  CLASSROOMS: 'CLASSROOMS',
  CLASS_MEMBERS: 'CLASS_MEMBERS',
  FEEDBACK_ADDITIO: 'FEEDBACK_ADDITIO',
  FEEDBACK_RESULT: 'FEEDBACK_RESULT',
  GRADE_ALGORITHM: 'GRADE_ALGORITHM',
  GRADE_ALGORITHMS: 'GRADE_ALGORITHMS',
  GROUPED_AVAILABLE_CLASSROOMS: 'GROUPED_AVAILABLE_CLASSROOMS',
  GROUPED_CLASSROOMS: 'GROUPED_CLASSROOMS',
  NOTEBOOK: 'NOTEBOOK',
  RESULTS: 'RESULTS',
  STUDENT_CLASS: 'STUDENT_CLASS',
  TASK: 'TASK',
  TASKS: 'TASKS',
  TASK_SUBMISSIONS: 'TASK_SUBMISSIONS',
}

const intervalTime = window?.def?.intervalAdditioProcessTime || 30000

export const useGetClassroomMaterials = ({ classId, enabled, userId }) => {
  const dispatch = useDispatch()
  const hideLoaderMaterials = () => {
    dispatch(hideLoader(true))
    return getClassroomMaterials({ classId, userId })
  }
  return useQuery(
    [CLASSROOM_KEYS.CLASSROOM, classId, userId],
    hideLoaderMaterials,
    {
      enabled: enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetClassroomStudent = ({ classId, enabled, userId }) => {
  const dispatch = useDispatch()
  const hideLoaderMaterials = () => {
    dispatch(hideLoader(true))
    return getClassByStudent({ classId, userId })
  }
  return useQuery(
    [CLASSROOM_KEYS.STUDENT_CLASS, classId, userId],
    hideLoaderMaterials,
    {
      enabled: enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetClassGradeAlgorithm = ({ classId, enabled, userId }) => {
  const dispatch = useDispatch()
  const hideLoaderGradeAlgorithm = () => {
    dispatch(hideLoader(true))
    return getClassGradeAlgorithm({ classId, userId })
  }
  return useQuery(
    [CLASSROOM_KEYS.GRADE_ALGORITHM, classId, userId],
    hideLoaderGradeAlgorithm,
    {
      enabled: enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetNotebook = ({ classId, enabled, userId }) => {
  const dispatch = useDispatch()
  const hideLoaderNotebook = () => {
    dispatch(hideLoader(true))
    return getNotebook({ classId, userId })
  }
  return useQuery(
    [CLASSROOM_KEYS.NOTEBOOK, classId, userId],
    hideLoaderNotebook,
    {
      enabled: enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetActivationAvailable = ({ classId, enabled }) => {
  const dispatch = useDispatch()
  const hideLoaderAvailableNotebook = () => {
    dispatch(hideLoader(true))
    return getActivationAvailableNotebook(classId)
  }
  return useQuery(
    [CLASSROOM_KEYS.AVAILABLE_NOTEBOOK, classId],
    hideLoaderAvailableNotebook,
    {
      enabled: enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useFeedbackActivateAdditio = (classroomId, enabled = false) => {
  const dispatch = useDispatch()
  const feedbackAdditio = () => {
    dispatch(hideLoader(true))
    return feedbackActivateAdditio(classroomId)
  }
  const getInterval = (data, query) => {
    const interval =
      !query.state?.error && data?.status === PROCESS_STATUS.PROCESSING
        ? intervalTime
        : false
    return interval
  }
  return useQuery([CLASSROOM_KEYS.FEEDBACK_ADDITIO], feedbackAdditio, {
    enabled: enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    refetchInterval: (data, query) => getInterval(data, query),
    refetchIntervalInBackground: true,
  })
}

export const useGetClassroomsByTeacherId = (teacherId) => {
  const dispatch = useDispatch()
  const classroomByTeacher = () => {
    dispatch(hideLoader(true))
    return getClassroomsByTeacherId(teacherId)
  }
  return useQuery([CLASSROOM_KEYS.CLASSROOMS, teacherId], classroomByTeacher, {
    staleTime: Infinity,
  })
}

export const useGetGroupedClassesByTeacher = ({
  enabled = true,
  teacherId,
}) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetGroupedClassesByTeacher = (teacherId) => {
    dispatch(hideLoader(true))
    return getGroupedClassesByTeacher(teacherId)
  }
  return useQuery(
    [CLASSROOM_KEYS.GROUPED_CLASSROOMS, teacherId],
    () => hideLoaderAndGetGroupedClassesByTeacher(teacherId),
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetGroupedAvailableClassesByTeacher = ({
  enabled = true,
  teacherId,
}) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetGroupedAvailableClassesByTeacher = (teacherId) => {
    dispatch(hideLoader(true))
    return getGroupedAvailableClassesByTeacher(teacherId)
  }
  return useQuery(
    [CLASSROOM_KEYS.GROUPED_AVAILABLE_CLASSROOMS, teacherId],
    () => hideLoaderAndGetGroupedAvailableClassesByTeacher(teacherId),
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetAvailableClassesByTeacher = (teacherId) => {
  const dispatch = useDispatch()
  const availableClassesByTeacher = (signal) => {
    dispatch(hideLoader(true))
    return getAvailableClassesByTeacher({ signal, teacherId })
  }
  return useQuery(
    [CLASSROOM_KEYS.AVAILABLE_CLASSES, teacherId],
    ({ signal }) => availableClassesByTeacher(signal),
    { staleTime: Infinity }
  )
}

export const usePostReorderClassrooms = () => {
  return useMutation(({ teacherId, updateUserClasses }) =>
    postReorderClassrooms({ teacherId, updateUserClasses })
  )
}

export const usePostJoinClassrooms = () => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const hideLoaderAndPostJoinClassrooms = ({ classIds, teacherId }) => {
    dispatch(hideLoader(true))
    return postJoinClassrooms({ classIds, teacherId })
  }
  return useMutation(
    ({ classIds, teacherId }) =>
      hideLoaderAndPostJoinClassrooms({ classIds, teacherId }),
    {
      onError: (error) => {
        addFeedback({
          dataTestId: 'error-feedback-join-classroom',
          id: 'error-feedback-join-classroom',
          text: error.message,
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: (data, variables) => {
        const { classIds, teacherId } = variables
        queryClient.setQueryData(
          [CLASSROOM_KEYS.GROUPED_AVAILABLE_CLASSROOMS, teacherId],
          (oldData) => {
            const classes = { ...oldData }
            Object.keys(classes).forEach((key) => {
              classes[key] = classes[key].filter(
                (clazz) => !classIds.includes(clazz.id)
              )
              if (!classes[key].length) delete classes[key]
            })
            return classes
          }
        )
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.GROUPED_CLASSROOMS],
        })
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.AVAILABLE_CLASSES],
        })
        queryClient.invalidateQueries({
          queryKey: [KEY_HOME],
        })
        addFeedback({
          dataTestId: 'success-feedback-join-classroom',
          id: 'success-feedback-join-classroom',
          text: t('Te has unido a {{numClasses}} clases con éxito', {
            numClasses: classIds.length,
          }),
          variant: TYPES_ALERTS.SUCCESS,
        })
        dispatch(hideLoader(false))
      },
    }
  )
}

export const usePatchRenameClassroom = () => {
  const queryClient = useQueryClient()
  const addFeedback = useSnackBarFeedback()
  return useMutation(
    ({ classId, name }) =>
      patchRenameClassroom({
        classId,
        name,
      }),
    {
      onError: (error) => {
        addFeedback({
          dataTestId: 'error-feedback-rename-classroom',
          id: 'error-feedback-rename-classroom',
          text: error.message,
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.GROUPED_CLASSROOMS],
        })
        queryClient.invalidateQueries({
          queryKey: [KEY_HOME],
        })
        queryClient.invalidateQueries({ queryKey: [CLASSROOM_KEYS.NOTEBOOK] })
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.GRADE_ALGORITHMS],
        })
      },
    }
  )
}

export const useDeleteClassroom = () => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()
  return useMutation(({ classId }) => deleteClassroom({ classId }), {
    onError: (error) => {
      addFeedback({
        dataTestId: 'error-feedback-delete-classroom',
        id: 'error-feedback-delete-classroom',
        text: error.message,
        variant: TYPES_ALERTS.ERROR,
      })
    },
    onSuccess: (data, variables) => {
      const { classroomName: className } = variables
      queryClient.invalidateQueries({ queryKey: [CLASSROOM_KEYS.CLASSROOM] })
      queryClient.invalidateQueries({
        queryKey: [CLASSROOM_KEYS.GROUPED_CLASSROOMS],
      })
      queryClient.invalidateQueries({ queryKey: [CLASSROOM_KEYS.NOTEBOOK] })
      queryClient.invalidateQueries({
        queryKey: [CLASSROOM_KEYS.GRADE_ALGORITHMS],
      })
      queryClient.invalidateQueries({
        queryKey: [CLASSROOM_KEYS.GROUPED_AVAILABLE_CLASSROOMS],
      })
      queryClient.invalidateQueries({
        queryKey: [KEY_HOME],
      })
      addFeedback({
        dataTestId: 'success-feedback-delete-classroom',
        id: 'success-feedback-delete-classroom',
        text: t('Has eliminado {{className}} correctamente', {
          className,
        }),
        variant: TYPES_ALERTS.SUCCESS,
      })
    },
  })
}

export const useGetGradeAlgorithms = () => {
  const dispatch = useDispatch()
  const hideLoaderAndCallApi = () => {
    dispatch(hideLoader(true))
    return getGradeAlgorithms()
  }
  return useQuery([CLASSROOM_KEYS.GRADE_ALGORITHMS], hideLoaderAndCallApi, {
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const usePatchGradeAlgorithms = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ classId, gradeAlgorithmType, numberOfAttempt, showFeedback }) =>
      patchGradeAlgorithms({
        classId,
        gradeAlgorithmType,
        numberOfAttempt,
        showFeedback,
      }),
    {
      onSuccess: ({ gradeSystem }, { classId, userId }) => {
        queryClient.setQueryData(
          [CLASSROOM_KEYS.GRADE_ALGORITHM, classId, userId],
          (oldData) => [
            { ...oldData[0], clazz: { ...oldData[0].clazz, gradeSystem } },
          ]
        )
      },
    }
  )
}

export const useFeedbackResult = (classroomId, enabled = false) => {
  const dispatch = useDispatch()
  const feedbackResult = () => {
    dispatch(hideLoader(true))
    return getFeedBackResult(classroomId)
  }
  const getInterval = (data, query) => {
    const interval =
      !query.state?.error && data?.status === PROCESS_STATUS.IN_PROGRESS
        ? intervalTime
        : false
    return interval
  }
  return useQuery([CLASSROOM_KEYS.FEEDBACK_RESULT], feedbackResult, {
    enabled: enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    refetchInterval: (data, query) => getInterval(data, query),
    refetchIntervalInBackground: true,
  })
}

export const useGetAvailableClassesBySchool = ({
  publicationId,
  schoolCode,
}) => {
  const dispatch = useDispatch()
  const disableAvailableClassesBySchoolLoader = () => {
    dispatch(hideLoader(true))
    return getAvailableClassesBySchool({
      publicationId,
      schoolCode,
    })
  }

  return useQuery(
    [CLASSROOM_KEYS.AVAILABLE_CLASSES_BY_SCHOOL, publicationId, schoolCode],
    disableAvailableClassesBySchoolLoader,
    {
      enabled: Boolean(schoolCode) && Boolean(publicationId),
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
    }
  )
}

export const usePostCreateClass = (teacherId) => {
  const queryClient = useQueryClient()
  const addFeedback = useSnackBarFeedback()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const hideLoaderAndPostCreateClass = ({
    className,
    classroomId,
    publicationId,
  }) => {
    dispatch(hideLoader(true))
    return postCreateClass({
      className,
      classroomId,
      publicationId,
      teacherId,
    })
  }
  return useMutation(
    ({ className, classroomId, publicationId }) =>
      hideLoaderAndPostCreateClass({
        className,
        classroomId,
        publicationId,
      }),
    {
      onError: () => {
        addFeedback({
          dataTestId: 'error-feedback-create-class',
          id: 'error-feedback-create-class',
          text: t('Ha ocurrido un problema'),
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: () => {
        dispatch(hideLoader(false))
        addFeedback({
          dataTestId: 'success-feedback-create-class',
          id: 'success-feedback-create-class',
          text: t('Se ha creado la clase correctamente'),
          variant: TYPES_ALERTS.SUCCESS,
        })
        queryClient.invalidateQueries({
          queryKey: [KEY_HOME],
        })
        queryClient.invalidateQueries({ queryKey: [CLASSROOM_KEYS.NOTEBOOK] })
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.GRADE_ALGORITHMS],
        })
      },
    }
  )
}

export const useGetClassesByStudent = ({ enabled = true, studentId }) => {
  const dispatch = useDispatch()
  const hideLoaderAndCallApi = () => {
    dispatch(hideLoader(true))
    return getClassesByStudent({ studentId })
  }
  return useQuery(
    [CLASSROOM_KEYS.CLASSES, studentId],
    () => hideLoaderAndCallApi(),
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetClassMembers = ({
  classId,
  enabled = true,
  pageStudents,
  sizeStudents,
}) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetClassMembers = () => {
    dispatch(hideLoader(true))
    return getClassMembers({
      classId,
      pageStudents,
      sizeStudents,
    })
  }
  return useQuery(
    [CLASSROOM_KEYS.CLASS_MEMBERS, classId, pageStudents],
    hideLoaderAndGetClassMembers,
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetTeacherTasksByClassId = ({
  classId,
  enabled,
  page,
  size,
  status,
  teacherId,
}) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetTeacherTasksByClassId = () => {
    dispatch(hideLoader(true))
    return getTeacherTasksByClassId({ classId, page, size, status, teacherId })
  }
  return useQuery(
    [CLASSROOM_KEYS.TASKS, classId, teacherId, status, page],
    hideLoaderAndGetTeacherTasksByClassId,
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useGetStudentTasksByClassId = ({
  classId,
  enabled,
  page,
  state,
  status,
  studentId,
}) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetStudentTasksByClassId = () => {
    dispatch(hideLoader(true))
    return getStudentTasksByClassId({ classId, state, status, studentId })
  }
  return useQuery(
    [CLASSROOM_KEYS.TASKS, classId, studentId, status, page, state],
    hideLoaderAndGetStudentTasksByClassId,
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useDeleteTask = (classId, teacherId, status, page) => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()

  return useMutation((taskId) => deleteTask(taskId), {
    onError: (error) => {
      addFeedback({
        dataTestId: 'error-feedback-delete-task',
        id: 'error-feedback-delete-task',
        text: error.message,
        variant: TYPES_ALERTS.ERROR,
      })
    },
    onSuccess: (data, taskId) => {
      queryClient.setQueryData(
        [CLASSROOM_KEYS.TASKS, classId, teacherId, status, page],
        (oldData) => {
          const { pager, tasks } = { ...oldData } || {}
          const newTasksList = tasks?.filter(({ id }) => id !== taskId)

          return {
            ...oldData,
            pager: {
              ...pager,
              endPageElement: pager?.endPageElement - 1,
              totalElements: pager?.totalElements - 1,
            },
            tasks: newTasksList,
          }
        }
      )
      addFeedback({
        dataTestId: 'success-feedback-delete-task',
        id: 'success-feedback-delete-task',
        text: t('Se ha eliminado la tarea correctamente.'),
        variant: TYPES_ALERTS.SUCCESS,
      })
    },
  })
}

export const useActivateTask = (classId, teacherId, status, page) => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()

  return useMutation((taskId) => patchActivateTask(taskId), {
    onError: (error) => {
      addFeedback({
        dataTestId: 'error-feedback-activate-task',
        id: 'error-feedback-activate-task',
        text: error.message,
        variant: TYPES_ALERTS.ERROR,
      })
    },
    onSuccess: (data, taskId) => {
      queryClient.setQueryData(
        [CLASSROOM_KEYS.TASKS, classId, teacherId, status, page],
        (oldData) => {
          const { tasks } = oldData || {}
          const index = tasks?.findIndex(({ id }) => id === taskId)
          if (index >= 0) oldData.tasks[index] = { ...data }
          return { ...oldData, tasks }
        }
      )
      addFeedback({
        dataTestId: 'success-feedback-activate-task',
        id: 'success-feedback-activate-task',
        text: t('Se ha activado la tarea correctamente.'),
        variant: TYPES_ALERTS.SUCCESS,
      })
    },
  })
}

export const useCreateTask = () => {
  return useMutation(
    ({
      activities,
      classId,
      deleteResultsOption,
      description,
      endDate,
      externalMaterials,
      name,
      startDate,
      students,
      teacherId,
    }) =>
      postCreateTask({
        activities,
        classId,
        deleteResultsOption,
        description,
        endDate,
        externalMaterials,
        name,
        startDate,
        students,
        teacherId,
      })
  )
}

export const useUpdateTask = () => {
  return useMutation(
    ({
      activities,
      classId,
      deleteResultsOption,
      description,
      endDate,
      externalMaterials,
      name,
      startDate,
      students,
      taskId,
      teacherId,
    }) =>
      patchUpdateTask({
        activities,
        classId,
        deleteResultsOption,
        description,
        endDate,
        externalMaterials,
        name,
        startDate,
        students,
        taskId,
        teacherId,
      })
  )
}

export const useGetTask = ({ enabled, taskId }) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetTask = () => {
    dispatch(hideLoader(true))
    return getTask(taskId)
  }
  return useQuery([CLASSROOM_KEYS.TASK, taskId], hideLoaderAndGetTask, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const useGetTaskSubmissions = ({ enabled, page, size, taskId }) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetTaskSubmissions = () => {
    dispatch(hideLoader(true))
    return getSubmissionsList({ page, size, taskId })
  }
  return useQuery(
    [CLASSROOM_KEYS.TASK_SUBMISSIONS, taskId],
    hideLoaderAndGetTaskSubmissions,
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
      staleTime: Infinity,
    }
  )
}

export const useCreateDraftTask = () => {
  return useMutation(
    ({
      activities,
      classId,
      description,
      endDate,
      externalMaterials,
      name,
      startDate,
      students,
      teacherId,
    }) =>
      postCreateDrafTask({
        activities,
        classId,
        description,
        endDate,
        externalMaterials,
        name,
        startDate,
        students,
        teacherId,
      })
  )
}

export const useUpdateTaskNotification = (taskId) => {
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()

  return useMutation(
    ({ taskId, userId }) => patchUpdateTaskNotification(taskId, userId),
    {
      onError: (error) => {
        addFeedback({
          dataTestId: 'error-feedback-notification-task',
          id: 'error-feedback-notification-task',
          text: error.message,
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: () => {
        queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => {
          const { tasks } = oldData.notifications
          const newTasks = tasks?.filter(({ taskId: id }) => id !== taskId)
          return {
            ...oldData,
            notifications: { tasks: [...newTasks] },
          }
        })
      },
    }
  )
}

export const useCloseTask = (classId, teacherId, status, page) => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()

  return useMutation((taskId) => patchCloseTask(taskId), {
    onError: (error) => {
      addFeedback({
        dataTestId: 'error-feedback-close-task',
        id: 'error-feedback-close-task',
        text: error.message,
        variant: TYPES_ALERTS.ERROR,
      })
    },
    onSuccess: (data, taskId) => {
      queryClient.setQueryData(
        [CLASSROOM_KEYS.TASKS, classId, teacherId, status, page],
        (oldData) => {
          const { tasks } = oldData || {}
          const index = tasks?.findIndex(({ id }) => id === taskId)
          if (index >= 0) oldData.tasks[index] = { ...data }
          return { ...oldData, tasks }
        }
      )
      queryClient.setQueryData(
        [CLASSROOM_KEYS.TASK_SUBMISSIONS, taskId],
        (oldData) => {
          return {
            ...oldData,
            status: TASK_STATUS.CLOSED,
          }
        }
      )
      addFeedback({
        dataTestId: 'success-feedback-close-task',
        id: 'success-feedback-close-task',
        text: t('Se ha finalizado el plazo de la tarea correctamente'),
        variant: TYPES_ALERTS.SUCCESS,
      })
    },
  })
}

export const useGetCheckResults = ({
  activityIds,
  classId,
  enabled,
  studentsIds,
}) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetCheckResults = () => {
    dispatch(hideLoader(true))
    return getCheckResults({ activityIds, classId, studentsIds })
  }
  return useQuery(
    [CLASSROOM_KEYS.RESULTS, activityIds, classId, studentsIds],
    hideLoaderAndGetCheckResults,
    {
      enabled,
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
    }
  )
}

export const useTaskStudentReview = () => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const queryClient = useQueryClient()

  return useMutation(
    ({ studentId, taskId }) => patchTaskStudentReview({ studentId, taskId }),
    {
      onError: (error) => {
        addFeedback({
          dataTestId: 'error-feedback-review-task',
          id: 'error-feedback-review-task',
          text: error.message,
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: (data, { taskId }) => {
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.TASK_SUBMISSIONS, taskId],
        })
        addFeedback({
          dataTestId: 'review-task-success-message',
          id: 'review-task-success-message',
          text: t('Se ha marcado la entrega como revisada correctamente.'),
          variant: TYPES_ALERTS.SUCCESS,
        })
      },
    }
  )
}
